import * as React from 'react';
import {
  Box,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Typography,
} from '@locus-taxy/disco-ui';
import { CustomAppPanel } from '../customApps/CustomAppUnavailable';
import { resolveAppConfig } from '../utils/resolveAppConfig';
import { useAppRegistry } from '../api/endpoints/useAppRegistry';
import LoadingSpinner from './LoadingSpinner';
import { isMfeAppConfig } from '../utils/customApp';
import type { CustomAppConfig } from '../utils/customApp';

interface CustomAppActionModalProps {
  /** Whether the modal is open */
  open: boolean;
  /** Handler to close the modal */
  onClose: () => void;
  /** Custom app configuration from the action */
  customApp: CustomAppConfig;
  /** Title for the modal (action label) */
  title?: string;
  /** Client ID */
  clientId: string;
  /** Entity name for config resolution */
  entityName: string;
  /** Entity data to pass to the MFE */
  entityData?: Record<string, unknown>;
}

/**
 * Modal that renders a Module Federation micro-frontend for an action
 * that has a customApp configuration, instead of the default Camunda form.
 */
export function CustomAppActionModal({
  open,
  onClose,
  customApp,
  title,
  clientId,
  entityName,
  entityData,
}: Readonly<CustomAppActionModalProps>) {
  // DP-4118: `appConfig.remoteApp` may point this app at another app's bundle.
  const { remoteAppFor, isReady } = useAppRegistry(clientId);
  const resolvedConfig = React.useMemo(() => {
    if (!isMfeAppConfig(customApp)) {
      return null;
    }
    return resolveAppConfig(
      customApp,
      entityName,
      clientId,
      remoteAppFor(customApp.appName)
    );
  }, [customApp, entityName, clientId, remoteAppFor]);

  if (!open || !resolvedConfig) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onOpenChange={next => {
        if (!next) {
          onClose();
        }
      }}
    >
      <DialogContent maxWidth="lg">
        <DialogHeader>
          <DialogTitle asChild>
            <Typography variant="h6" component="span">
              {title ?? customApp.name}
            </Typography>
          </DialogTitle>
        </DialogHeader>
        <Box className="flex min-h-[400px] flex-col overflow-hidden border-t pt-4">
          {isReady ? (
            /* Panel-sized failure, not the app-wide one: the default control
               is "Refresh Page" wired to window.location.reload(), which in a
               modal throws away the modal and whatever the user had open
               (DP-6140). CustomAppPanel's retry re-fetches this app alone. */
            <CustomAppPanel
              config={resolvedConfig}
              clientId={clientId}
              entityName={entityName}
              entityTitle={title ?? customApp.name}
              entityData={entityData ?? {}}
            />
          ) : (
            <LoadingSpinner />
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
}
